import { transition, trigger } from '@angular/animations';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Params, Router } from '@angular/router';
import { AssetColorLabel, AssetDto, AssetStatus, GalleryDto, Section, TenantDto } from '@nusign/picflow-backend-types';
import { Subject, filter, firstValueFrom, lastValueFrom, switchMap, take, takeUntil } from 'rxjs';
import { ListAssetsItemComponent } from '../../../account/components/list-assets-item/list-assets-item.component';
import { fadeDownIf } from '../../../animations/fade-down.if.animation';
import { fadeUpIf } from '../../../animations/fade-up.if.animation';
import { fadeIfDelayed } from '../../../animations/fade.if-delayed.animation';
import { moveRightIf } from '../../../animations/move-right.if.animation';
import { widthChangeState } from '../../../animations/width-change-state';
import { billingScopes } from '../../../billing-consts';
import { Rect } from '../../../classes/rect.class';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { UploadFile } from '../../../classes/upload-obj.class';
import { defaultColorLabels } from '../../../configs/default-color-labels';
import {
  GALLERY_DOWNLOAD_SIZE,
  assetLoadSettings,
  doubleClickTime,
  dragScrollThreshold,
  maxZipRequestSize,
} from '../../../consts';
import { AssetStatusButtonExtraContextComponent } from '../../../gallery/components/asset-status-button-extra-context/asset-status-button-extra-context.component';
import { ColorLabelContextComponent } from '../../../gallery/components/color-label-context/color-label-context.component';
import { DeleteAssetsDialogComponent } from '../../../gallery/dialogs/delete-assets-dialog/delete-assets-dialog.component';
import { ManualConfirmDialogComponent } from '../../../gallery/dialogs/manual-confirm-dialog/manual-confirm-dialog.component';
import { ShareAssetDialogComponent } from '../../../gallery/dialogs/share-asset-dialog/share-asset-dialog.component';
import { ShareGalleryDialogComponent } from '../../../gallery/dialogs/share-gallery-dialog/share-gallery-dialog.component';
import { calcLegacyTheme } from '../../../gallery/lib/calc-legacy-theme';
import { getAssetDimentions } from '../../../gallery/lib/get-asset-dimentions';
import { sortAssetSection } from '../../../gallery/lib/sort-asset-section';
import { GallerySettingsMenuComponent } from '../../../gallery/menus/gallery-settings-menu/gallery-settings-menu.component';
import { AssetStatusColorPipe } from '../../../gallery/pipes/asset-status-color.pipe';
import { AssetStatusIconPipe } from '../../../gallery/pipes/asset-status-icon.pipe';
import { AssetStatusNamePipe } from '../../../gallery/pipes/asset-status-name.pipe';
import { AssetPageDataService } from '../../../gallery/services/asset-page-data.service';
import { GROWTHBOOK } from '../../../growthbook';
import { ArchiveSize } from '../../../interfaces/archive-request.interface';
import { calcGalleryPermissions } from '../../../lib/access-level-helpers';
import { arraysEqual } from '../../../lib/arrays-equal';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { changeCoverAsset } from '../../../lib/change-cover-asset';
import { convertGalleryDownloadSize } from '../../../lib/convert-gallery-download-size';
import { copy } from '../../../lib/copy';
import { debounce } from '../../../lib/debounce';
import { getScrollbarWidth } from '../../../lib/get-scrollbar-width';
import { isMetaKey } from '../../../lib/is-meta-key';
import { forkJoinSafe } from '../../../lib/safe-fork-join';
import { sleep } from '../../../lib/sleep';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { PICFLOW_CHECKOUT } from '../../../picflow-checkout';
import { ActivityService } from '../../../services/activity.service';
import { ArchiveService } from '../../../services/archive.service';
import { AssetApprovalLimitService } from '../../../services/asset-approval-limit.service';
import { AssetFeedbackService } from '../../../services/asset-feedback.service';
import { AssetMoveService } from '../../../services/asset-move.service';
import { AssetPreGenerateService } from '../../../services/asset-pre-generate.service';
import { AssetService } from '../../../services/asset.service';
import { BrowserService } from '../../../services/browser.service';
import { ContextMenuService } from '../../../services/context-menu.service';
import { DialogService } from '../../../services/dialog.service';
import { DownloadAssetService } from '../../../services/download-asset.service';
import { ExportGalleryService } from '../../../services/export-gallery.service';
import { GalleryAccessLevelService } from '../../../services/gallery-access-level.service';
import { GalleryThemeService } from '../../../services/gallery-theme.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { StateService } from '../../../services/state.service';
import { StorageService } from '../../../services/storage.service';
import { TenantService } from '../../../services/tenant.service';
import { ToastService } from '../../../services/toast.service';
import { UploadService } from '../../../services/upload.service';
import { DisplayGuestEmailDto, DisplayUserEmailDto } from '../../../services/visitor.service';
import { DropComponent } from '../../../shared-extra/components/drop/drop.component';
import { GalleryDownloadMenuOptionsComponent } from '../../../shared-extra/components/gallery-download-menu-options/gallery-download-menu-options.component';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { ButtonMenuItemComponent } from '../../../shared/components/button-menu-item/button-menu-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SmoothCounterComponent } from '../../../shared/components/smooth-counter/smooth-counter.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { AssetUrlPipe } from '../../../shared/pipes/asset-url.pipe';
import { AssetVersionUrlPipe } from '../../../shared/pipes/asset-version-url.pipe';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { ApprovalLimitReachedDialogComponent } from '../../dialogs/approval-limit-reached-dialog/approval-limit-reached-dialog.component';
import { DownloadAllDialogComponent } from '../../dialogs/download-all-dialog/download-all-dialog.component';
import { ExportGalleryDialogComponent } from '../../dialogs/export-gallery-dialog/export-gallery-dialog.component';
import { MoveAssetsDialogComponent } from '../../dialogs/move-assets-dialog/move-assets-dialog.component';
import { Filter } from '../../interfaces/filter.interface';
import { filterAssets } from '../../lib/filter-assets';
import { allowedAssetSortingAndGroupingValues, getAssetGroups } from '../../lib/sort-assets-sorting-and-grouping';
import { AssetGroup } from '../../types/asset-group.type';
import { AdvancedModeContentHeaderComponent } from '../advanced-mode-content-header/advanced-mode-content-header.component';
import { AdvancedModeSidebarComponent } from '../advanced-mode-sidebar/advanced-mode-sidebar.component';
import { getNewAsset } from './get-new-asset';

@Component({
  selector: 'app-advanced-mode',
  templateUrl: './advanced-mode.component.html',
  styleUrls: ['./advanced-mode.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('emptyParent', [transition(':enter', [])]),
    trigger('sidebar', moveRightIf),
    trigger('content', widthChangeState('100%', 'calc(100% - 300px)')),
    trigger('selectedAssetsActions', fadeUpIf),
    trigger('assetActionsTop', fadeDownIf),
    trigger('assetActionsBottom', fadeUpIf),
    trigger('loadingScreen', fadeIfDelayed),
  ],
  imports: [
    AdvancedModeSidebarComponent,
    AdvancedModeContentHeaderComponent,
    DropComponent,
    ButtonDirective,
    SvgComponent,
    FormsModule,
    ResizeDirective,
    ContextMenuComponent,
    ButtonMenuItemComponent,
    GalleryDownloadMenuOptionsComponent,
    TooltipDirective,
    LoadingIndicatorComponent,
    UpgradeBadgeComponent,
    AssetUrlPipe,
    TranslatePipe,
    SmoothCounterComponent,
    AssetStatusIconPipe,
    AssetStatusColorPipe,
    AssetStatusNamePipe,
    AssetStatusButtonExtraContextComponent,
    ColorLabelContextComponent,
    ListAssetsItemComponent,
  ],
})
export class AdvancedModeComponent implements OnInit, OnChanges, OnDestroy {
  private assetService = inject(AssetService);
  private accessLevelService = inject(GalleryAccessLevelService);
  private storage = inject(StorageService);
  private state = inject(StateService);
  private dialog = inject(DialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private contextMenuService = inject(ContextMenuService);
  private uploadService = inject(UploadService);
  private sectionService = inject(SectionService);
  private galleryService = inject(GalleryService);
  private galleryThemeService = inject(GalleryThemeService);
  private assetFeedbackService = inject(AssetFeedbackService);
  private tenantService = inject(TenantService);
  private archiveService = inject(ArchiveService);
  private toasts = inject(ToastService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private browserService = inject(BrowserService);
  private permissionService = inject(PermissionService);
  private assetPageDataService = inject(AssetPageDataService);
  private versionUrlPipe = inject(AssetVersionUrlPipe);
  private assetPreGenerateService = inject(AssetPreGenerateService);
  private assetApprovalLimitService = inject(AssetApprovalLimitService);
  private downloadAssetService = inject(DownloadAssetService);
  private assetMoveService = inject(AssetMoveService);
  private activityService = inject(ActivityService);
  private exportGalleryService = inject(ExportGalleryService);
  private platformId = inject(PLATFORM_ID);
  private check = inject(ChangeDetectorRef);
  private growthBook = inject(GROWTHBOOK);
  private checkoutService = inject(PICFLOW_CHECKOUT);

  readonly gallery = input<GalleryDto>(undefined);
  theme = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    return this.gallery().theme || calcLegacyTheme(this.gallery());
  });
  @Input() sections: Section[];
  @Input() clientMode = false;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('assets') _assets: AssetDto[];
  @Input() blockShortcuts: boolean;
  private readonly accessLevel = toSignal(
    toObservable(this.gallery).pipe(
      filter(Boolean),
      switchMap(gallery => this.accessLevelService.depricatedGetAccessLevel(gallery))
    )
  );
  public galleryPermissions = computed(() => calcGalleryPermissions(this.gallery(), this.accessLevel()));
  assets: AssetDto[] = [];
  assetGroups: AssetGroup[] = [];
  sorting = 'auto';
  grouping = 'section';
  showSidebar = true;
  columnCount: number;
  cellSize: number;
  assetWidth: string;
  defaultAssetSize = 340;
  assetTargetSize = this.defaultAssetSize;
  assetMinSize = 180;
  assetMaxSize = 500;
  selectedAssets: Map<string, boolean> = new Map();
  filters: Filter[] = [];
  searchTerm: string;
  searchTermExact: boolean;
  selectedAssetsCount = 0;
  firstSelected: AssetDto;
  lastSelected: AssetDto;
  lastClicked: AssetDto;
  firstSelectedGroup: AssetGroup;
  selectedCombined: { status: AssetStatus; color_label: AssetColorLabel; is_liked: boolean };
  inView: { [key: string]: boolean } = {};
  uploadEl: ElementRef;
  totalSize = 0;
  lastClickTime = 0;
  uploadingCount = 0;
  assetTouchStartPos: Rect;
  // multiTouchTimeout: NodeJS.Timeout;
  potentialDragging: AssetDto;
  potentialDraggingStartPos: Rect;
  mousePos: Rect;
  scrollingPage: number;
  isMobile: boolean;
  isDesktop: boolean;
  dragging: { index: number; groupIndex: number; pos?: Rect } = undefined;
  hoverAsset: AssetDto;
  contextOpenAsset: AssetDto;
  browserPlatform: boolean;
  selectedGroups: { [key: string]: boolean } = {};
  downloadingSelected = false;
  notifiedAboutSorting = false;
  colorLabels = computed(() => {
    const gallery = this.gallery();
    if (this.permissionService.canDisplayElement(billingScopes.asset.customLabels)) {
      return gallery.color_labels;
    }
    return defaultColorLabels;
  });
  groupings = allowedAssetSortingAndGroupingValues;
  downloadSizes: ArchiveSize[] = ['original', 'large', 'medium', 'small'];
  allowedFeedbackOptions = computed(() => {
    const gallery = this.gallery();
    const galleryPermissions = this.galleryPermissions();
    if (!this.galleryPermissions().can_edit) {
      // in client mode we dont even want to show non enabled feedback options (in advanced mode sidebar and favourites)
      return this.permissionService.getAllowedAndEnabledFeedbackOptions(
        gallery,
        galleryPermissions.can_review_if_authenticated
      );
    }
    return this.permissionService.getAllowedFeedbackOptions(galleryPermissions.can_review_if_authenticated);
  });
  allowedAndEnabledFeedbackOptions = computed(() => {
    return this.permissionService.getAllowedAndEnabledFeedbackOptions(
      this.gallery(),
      this.galleryPermissions()?.can_review_if_authenticated
    );
  });
  assetPage: boolean;
  people: { userOrGuest: DisplayUserEmailDto | DisplayGuestEmailDto; assetIds: string[]; type: 'user' | 'guest' }[];
  destroyed$ = new Subject<void>();

  loading = signal(true);

  @ViewChild('sidebar') sidebar: AdvancedModeSidebarComponent;
  @ViewChild('header') header: AdvancedModeContentHeaderComponent;
  @ViewChild('content') content: ElementRef;
  @ViewChild('assetsContainer') assetsContainer: ElementRef<HTMLDivElement>;
  @HostListener('window:resize', ['$event']) winResize() {
    if (!this.gallery()) return;
    this.calculateColumnCount();
    this.calcInView();
    const screenSize = ScreenSizes.screenSize();
    this.isMobile = screenSize === 'mobile';
    this.isDesktop = screenSize === 'desktop';
  }
  @HostListener('window:scroll', ['$event']) docScroll() {
    this.calcInView();
  }
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (this.blockShortcuts) return;
    if (this.dialog.dialog$.value) return;
    this.keyDown(event);
  }

  constructor() {
    this.browserPlatform = isPlatformBrowser(this.platformId);
    if (!this.browserPlatform) return;
    this.loadPreferences();

    if (ScreenSizes.screenSize() === 'mobile') {
      this.showSidebar = false;
      this.assetTargetSize = 150;
    }
    this.calculateColumnCount();
    this.isMobile = ScreenSizes.screenSize() === 'mobile';
    this.isDesktop = ScreenSizes.screenSize() === 'desktop';

    this.router.events.pipe(takeUntil(this.destroyed$)).subscribe(event => {
      if (event instanceof NavigationStart) {
        this.assetPage = this.browserService.urlIsAssetUrl(event.url);
        if (!this.assetPage) {
          // recalculating filters because of tags. A tag could have been deleted while it was being filtered
          this.sidebar?.createDynamicFilters()?.subscribe();
          this.sidebar?.calcAssetCountsDebounced();
        }
      }
    });

    this.assetMoveService.assetMove$.pipe(takeUntil(this.destroyed$), filter(Boolean)).subscribe(event => {
      if (!this._assets) return;
      const asset = this._assets.find(a => a.id === event.assetId);
      if (!asset) return;
      this.onMovedOut(asset);
    });

    this.assetService.updates
      .pipe(
        takeUntil(this.destroyed$),
        filter(event => event && !event.isOptimistic)
      )
      .subscribe(event => {
        if (!this._assets) return;
        if (event.action === 'created') {
          if (this._assets.find(img => img.id === event.asset.id)) return;
          this._assets.push(event.asset);
        }
        if (event.action === 'deleted') {
          const index = this._assets.findIndex(img => img.id === event.asset.id);
          if (index !== -1) {
            this._assets.splice(index, 1);
            this.calcPeopleDebounced();
          }
        }
        if (event.action === 'updated') {
          const index = this._assets.findIndex(img => img.id === event.asset.id);
          if (index !== -1) {
            const statusChanged = this._assets[index].version_data.status !== event.asset.version_data.status;
            Object.assign(this._assets[index], event.asset);
            this.calcPeopleDebounced();

            if (statusChanged) {
              this.sidebar?.createDynamicFilters()?.subscribe();
            }
          }
        }

        this.filterChange();
        this.calcSelectedCombined();
      });
  }

  ngOnInit() {
    if (!this.browserPlatform) return;
    this.assets = this._assets;
    this.handleFiltersFromQuery();
    this.check.detectChanges();
  }

  private handleFiltersFromQuery() {
    const filterString = this.route.snapshot.queryParams.filters;
    if (filterString) {
      let filters: Filter[];
      try {
        filters = JSON.parse(filterString);
      } catch (err) {
        console.warn('Not able to parse filters from url');
        console.error(err);
      }
      if (filters) {
        this.filters = filters;
        this.filterChange();
      }
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (isPlatformServer(this.platformId)) return;
    if (changes.gallery && changes.gallery.currentValue.id !== changes.gallery.previousValue?.id) {
      this.unselectAll();
      this.setupSelectedAssets();
      this.filterChange();
      this.calcPeople();
    }
    if (changes._assets) this.filterChange();
  }

  private async calcPeople() {
    if (!this.people) this.people = [];
    await firstValueFrom(this.assetPageDataService.getGalleryPeople(this.gallery(), this.people));
    this.filterChange();
    this.check.detectChanges();
  }

  private calcPeopleDebounced = debounce(() => {
    this.calcPeople();
  }, 100);

  private setupSelectedAssets() {
    this.selectedAssets = new Map();
    this._assets.forEach(img => this.selectedAssets.set(img.id, false));
  }

  stopPropagation(event: Event) {
    this.contextMenuService.closeContext();
    event.stopPropagation();
  }

  resetFilters() {
    this.filters = [];
    this.header.setSearch('');
    this.filterChange();
    this.sidebar?.resetFilters();
    this.setAdvancedModeAssets();
  }

  search({ term, exactMatch }: { term: string; exactMatch: boolean }) {
    this.searchTerm = term;
    this.searchTermExact = exactMatch;
    this.filterChange();
  }

  async onSideMenuToggle() {
    this.showSidebar = !this.showSidebar;
    this.savePreferences();
    await sleep(100);
    this.calculateColumnCount();
    this.calcInView();
  }

  calculateColumnCount() {
    if (isPlatformServer(this.platformId)) return 8;
    let width = window.innerWidth - getScrollbarWidth();
    if (this.showSidebar) width -= 300;
    this.columnCount = Math.ceil(width / this.assetTargetSize);
    this.assetWidth = `calc(${100 / this.columnCount}% + 1px)`;
    this.cellSize = width / this.columnCount;
  }

  filterChange = debounce(async () => {
    const feedbackMap = await firstValueFrom(
      this.activityService.getGalleryAssetUserFeedbackMap(this.gallery(), this._assets)
    );

    this.assets = filterAssets(
      this._assets,
      feedbackMap,
      this.filters,
      this.searchTerm,
      this.searchTermExact,
      this.state.unseenActivityCountMap
    );

    if (this.sorting && this.sorting !== 'auto') {
      this.assets = sortAssetSection(this.assets, this.sorting);
    } else {
      // sort like in designer
      const newAssets = [];
      this.sections.forEach(section => {
        const assets = this.assets.filter(asset => asset.section === section.id);
        const sortedAssets = sortAssetSection(assets, section.sorting);
        newAssets.push(...sortedAssets);
      });
      this.assets = newAssets;
    }

    this.setAdvancedModeAssets();

    this.setGroupedAssets();
    this.loading.set(false);
    this.check.detectChanges();
    await sleep(0);
    this.calcInView();
  }, 100);

  getGroupedAssets(): AssetGroup[] {
    return getAssetGroups(this.assets, this.grouping, this.sections, this.colorLabels());
  }

  setGroupedAssets() {
    const groups = this.getGroupedAssets();
    if (JSON.stringify(groups) !== JSON.stringify(this.assetGroups)) {
      this.assetGroups = groups;
    }
  }

  changeAssetSize(size: number) {
    this.assetTargetSize = size;
    this.savePreferences();
    this.calculateColumnCount();
    this.calcInView();
  }

  changeSorting(sorting: string) {
    this.sorting = sorting;
    this.filterChange();
    this.savePreferences();
  }

  changeGrouping(grouping: string) {
    this.grouping = grouping;
    this.filterChange();
    this.savePreferences();
  }

  private checkAssetInView() {
    if (!this.lastSelected) return;
    const el = document.getElementById('asset-' + this.lastSelected.id);
    if (!el) return;
    const bounds = el.getBoundingClientRect();
    const contentBounds = this.content.nativeElement.getBoundingClientRect();
    if (bounds.top < contentBounds.top) {
      this.content.nativeElement.scrollTop += bounds.top - contentBounds.top - 20;
    } else if (bounds.bottom > window.innerHeight - 20) {
      const bottom = bounds.bottom - contentBounds.height;
      this.content.nativeElement.scrollTop += bottom - contentBounds.top + 20;
    }
  }

  private unselectAssets(assets: AssetDto[]) {
    assets.forEach(asset => {
      if (!this.selectedAssets.get(asset.id)) return;
      this.selectedAssets.set(asset.id, false);
      this.selectedAssetsCount -= 1;
    });
    if (!this.getSelectedAssets().length) {
      this.firstSelected = undefined;
      this.lastSelected = undefined;
    }
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.checkGroups();

    this.setAdvancedModeAssets();
  }

  unselectAsset = (asset: AssetDto) => this.unselectAssets([asset]);
  unselectAll = () => {
    this.unselectAssets(this._assets);
    this.selectedAssetsCount = 0;
    if (this.dragging) {
      this.dragging = undefined;
      this.potentialDragging = undefined;
    }
  };

  private selectAssets(assets: AssetDto[]) {
    document.activeElement?.['blur']();
    const selectedAssets = this.getSelectedAssets();
    if (!selectedAssets.length) this.firstSelected = assets[0];
    this.lastSelected = assets.slice().pop();
    this.lastClicked = this.lastSelected;
    assets.forEach(asset => {
      if (this.selectedAssets.get(asset.id)) return;
      this.selectedAssets.set(asset.id, true);
      this.selectedAssetsCount += 1;
    });
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.checkAssetInView();
    this.checkGroups();
    this.setAdvancedModeAssets();
  }

  selectAsset = (asset: AssetDto) => this.selectAssets([asset]);
  selectAll = () => this.selectAssets(this.assets);

  assetClick(asset: AssetDto, event: MouseEvent) {
    if (this.dragging) return;
    const metaKey = isMetaKey(event);
    const isTouchCapable = !!this.assetTouchStartPos;

    const isDoubleTap = Date.now() - this.lastClickTime < doubleClickTime && this.lastClicked?.id === asset.id;
    this.lastClickTime = Date.now();

    if (metaKey || (isTouchCapable && !isDoubleTap)) {
      if (this.selectedAssets.get(asset.id)) {
        this.unselectAsset(asset);
      } else this.selectAsset(asset);
    } else if (event.shiftKey && this.firstSelected) {
      const _assets = [].concat(...this.assetGroups.map(group => group.assets));

      const range = [
        _assets.findIndex(img => img.id === this.firstSelected.id),
        _assets.findIndex(img => img.id === asset.id),
      ];
      if (range[0] > range[1]) range.reverse();

      const assets = _assets.filter((img, i) => i >= range[0] && i <= range[1]);

      this.unselectAll();
      this.selectAssets(assets);
    } else {
      if (isDoubleTap) this.openAsset(asset);
      if (this.selectedAssetsCount === 1 && this.selectedAssets.get(asset.id)) {
        this.unselectAll();
      } else {
        this.unselectAll();
        this.selectAsset(asset);
      }
    }
  }

  private calcSelectedCombined() {
    const selectedAssets = this.getSelectedAssets();
    if (!selectedAssets.length) return;

    this.selectedCombined = {
      is_liked: selectedAssets[0]['is_liked'],
      status: selectedAssets[0].status,
      color_label: selectedAssets[0].color_label,
    };

    for (const key in this.selectedCombined) {
      if (selectedAssets.find(img => img[key] !== this.selectedCombined[key])) {
        this.selectedCombined[key] = undefined;
      }
    }
  }

  private calcTotalSize() {
    this.totalSize = 0;
    this.assets.forEach(img => {
      if (!this.selectedAssets.get(img.id)) return;
      this.totalSize += img.version_data.size;
    });
  }

  allowDownloadSelected() {
    if (this.galleryPermissions().can_edit) return true;
    const gallery = this.gallery();
    if (!gallery.download_full_gallery) return false;
    if (!gallery.approved_downloads_only) return true;
    return this.getSelectedAssets().some(asset => asset.status === 'approved');
  }

  filterAssetsForDownload(assets: AssetDto[]) {
    if (this.galleryPermissions().can_edit) return assets;
    if (!this.gallery().approved_downloads_only) return assets;
    return assets.filter(asset => asset.status === 'approved');
  }

  async downloadSelected(size: ArchiveSize) {
    if (this.downloadingSelected) return;
    const assets = this.filterAssetsForDownload(this.getSelectedAssets());
    if (!assets.length) return;

    this.downloadingSelected = true;

    if (assets.length <= 1) {
      await this.downloadAssetService.downloadAssetVersions(
        assets.map(asset => asset.version_data),
        assets[0].type,
        assets.map(asset => asset.watermark_enabled),
        this.gallery().id,
        GALLERY_DOWNLOAD_SIZE[size]
      );
      this.downloadingSelected = false;
      return;
    }

    const galleryData = {
      gallery: this.gallery(),
      assets,
    };

    const requests = await firstValueFrom(
      this.archiveService.createGalleryArchivesWithAssets([galleryData], size, false)
    );

    const totalDownloadSize = requests.reduce((acc, curr) => acc + curr.totalSize, 0n);

    if (totalDownloadSize > maxZipRequestSize) {
      const resp = await this.dialog.open(DownloadAllDialogComponent, {
        totalSize: totalDownloadSize,
        amAllowed: this.gallery().client_advanced_mode_enabled,
        galleries: [this.gallery()],
      });
      this.downloadingSelected = false;
      if (!resp) return;
      if (resp === 'am') return this.router.navigate(['./advanced'], { relativeTo: this.route });
      this.downloadingSelected = true;
    }

    await lastValueFrom(
      forkJoinSafe(
        requests.map((request, index) => {
          return this.archiveService
            .requestArchiveCreation(
              request.request,
              this.gallery().id,
              request.assetCount,
              request.totalSize,
              requests.length === 1 ? undefined : { index, total: requests.length }
            )
            .pipe(switchMap(archive => this.archiveService.onArchiveDone(archive).pipe(take(1))));
        })
      )
    );
    this.downloadingSelected = false;
  }

  getDownloadSizes(): ArchiveSize[] {
    const assets = Array.from(this.selectedAssets.entries())
      .filter(([, v]) => v)
      .map(([id]) => this.assets.find(img => img.id === id));

    const maxShortEdge = this.archiveService.getMaxShortEdge(assets);
    const sizes: ArchiveSize[] = [];
    const enabledSizes = convertGalleryDownloadSize(this.gallery().download_media_size);

    this.downloadSizes.forEach(size => {
      if (maxShortEdge < GALLERY_DOWNLOAD_SIZE[size]) return;
      if (!enabledSizes[size]) return;
      sizes.push(size);
    });

    return sizes;
  }

  async deleteSelectedAssets() {
    const assetsToDelete = Array.from(this.selectedAssets.entries())
      .filter(a => a[1])
      .map(item => this.assets.find(img => img.id === item[0]))
      .filter(a => a);

    if (!assetsToDelete.length) return;

    if (assetsToDelete.length > 1) {
      const resp = await this.dialog.open(DeleteAssetsDialogComponent, {
        assets: assetsToDelete,
        gallery: this.gallery(),
      });
      if (!resp) return;
    } else {
      await this.assetService.deleteAsset(assetsToDelete[0]);
    }

    // Removing deleted assets
    this._assets = this._assets.filter(img => !assetsToDelete.find(_img => img.id === _img.id));

    this.unselectAll();
    this.filterChange();
    this.setupSelectedAssets();
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.analytics.track('assets_deleted', true);
  }

  async onMovedOut(img: AssetDto) {
    this.unselectAsset(img);
    this._assets = this._assets.filter(_img => _img.id !== img.id);
    // this.assetService.handleAssetOrderChange(img);
    this.filterChange();
    this.setupSelectedAssets();
    this.calcTotalSize();
    this.calcSelectedCombined();
    this.sidebar?.createDynamicFilters()?.subscribe();
  }

  onNewVersion() {
    this.calculateColumnCount();
    this.calcInView();
    this.filterChange();
    this.sidebar?.createDynamicFilters()?.subscribe();
  }

  private loadPreferences() {
    if (!this.storage.has('advanced-mode-preferences')) return;
    const preferences = this.storage.get<{
      assetTargetSize: number;
      showSidebar: boolean;
      sorting: string;
      grouping: string;
      sortingAndGrouping?: string;
    }>('advanced-mode-preferences');
    if (preferences.assetTargetSize) this.assetTargetSize = preferences.assetTargetSize;
    if (preferences.showSidebar !== undefined) this.showSidebar = preferences.showSidebar;
    if (preferences.sorting !== undefined) this.sorting = preferences.sorting;
    if (preferences.grouping !== undefined) this.grouping = preferences.grouping;
    if (preferences.sortingAndGrouping !== undefined) {
      // legacy, can be removed at some point
      const v = preferences.sortingAndGrouping;
      if (this.groupings.includes(v)) {
        this.grouping = v;
      } else {
        this.sorting = v;
      }
    }
  }

  private savePreferences() {
    this.storage.set('advanced-mode-preferences', {
      assetTargetSize: this.assetTargetSize,
      showSidebar: this.showSidebar,
      sorting: this.sorting,
      grouping: this.grouping,
    });
  }

  private async likeAssets(assets: AssetDto[]) {
    if (!this.gallery().enabled_feedback_options?.includes('favourite')) return;
    if (!(await this.checkAuth())) return;

    assets.forEach(asset => this.assetFeedbackService.toggleLike(asset, this.gallery()));
    this.calcSelectedCombined();
    this.filterChange();
  }

  private unlikeAssets(assets: AssetDto[]) {
    if (!this.gallery().enabled_feedback_options?.includes('favourite')) return;
    assets.forEach(asset => {
      this.assetFeedbackService.toggleLike(asset, this.gallery());
    });
    this.calcSelectedCombined();
    this.filterChange();
  }

  toggleAssetLike(asset: AssetDto) {
    if (asset['is_liked']) this.unlikeAssets([asset]);
    else this.likeAssets([asset]);
  }

  toggleLikeSelectedAssets() {
    if (!this.selectedCombined) return;
    const liked = this.selectedCombined.is_liked;
    if (liked === undefined || liked === false) {
      // If some assets are liked and some are not then we like them all
      this.likeSelectedAssets();
    } else if (liked === true) {
      this.unlikeSelectedAssets();
    }
  }

  private likeSelectedAssets() {
    const assets = this.getSelectedAssets().filter(img => !img['is_liked']);
    this.likeAssets(assets);
  }

  private unlikeSelectedAssets() {
    const assets = this.getSelectedAssets().filter(img => img['is_liked']);
    this.unlikeAssets(assets);
  }

  async updateAssetStatus(asset: AssetDto, status: AssetStatus) {
    const gallery = this.gallery();
    if (!gallery.enabled_feedback_options?.includes('status')) return;
    if (asset.status === status) return;
    if (!this.galleryPermissions().can_edit && !this.allowedFeedbackOptions().includes('status')) return;
    if (!(await this.checkAuth())) return;
    await this.assetFeedbackService.updateAssetStatus(asset, gallery, status, this.clientMode);
    this.filterChange();
    this.calcSelectedCombined();
  }

  updateSelectedAssetStatus(status: AssetStatus, isKeyboardShortcut = false) {
    const gallery = this.gallery();
    if (gallery.enabled_asset_statuses && !gallery.enabled_asset_statuses.includes(status)) {
      if (status === 'rejected') status = 'no_status';
      else return;
    }
    const selectedAssets = this.getSelectedAssets();
    if (!selectedAssets.length) return;
    if (!gallery.approval_limits || gallery.approval_limits < 0) {
      // negative means soft limit
      return selectedAssets.forEach(img => this.updateAssetStatus(img, status));
    }
    if (status === 'approved') {
      return this.assetApprovalLimitService
        .getApprovedAssetCountForGallery(gallery.id)
        .pipe(take(1))
        .subscribe(count => {
          const remaining = Math.abs(this.gallery().approval_limits) - count;
          const willApproveCount = selectedAssets.filter(img => img.status !== 'approved').length;
          if (remaining < willApproveCount) {
            this.dialog.open(ApprovalLimitReachedDialogComponent, { remaining, count, gallery: this.gallery() });
            return;
          }

          if (isKeyboardShortcut && selectedAssets.length) {
            this.analytics.reportKeyboardShortcut('asset-status', 'advanced-mode', {
              status,
              assetsAffected: selectedAssets.length,
            });
          }
          selectedAssets.forEach(img => this.updateAssetStatus(img, status));
        });
    }
  }

  async updateAssetColorLabel(asset: AssetDto, color_label: AssetColorLabel) {
    const gallery = this.gallery();
    if (!gallery.enabled_feedback_options?.includes('color_label')) return;
    if (asset.color_label === color_label) return;
    if (!this.galleryPermissions().can_edit && !this.allowedFeedbackOptions().includes('color_label')) return;
    if (!(await this.checkAuth())) return;
    await this.assetFeedbackService.updateColorLabel(asset, gallery, color_label, this.clientMode);

    this.filterChange();
    this.calcSelectedCombined();
  }

  async updateSelectedAssetColorLabel(color_label: AssetColorLabel, isKeyboardShortcut = false) {
    const selectedAssets = this.getSelectedAssets();
    selectedAssets.forEach(img => this.updateAssetColorLabel(img, color_label));

    if (!selectedAssets.length) return;
    if (isKeyboardShortcut) {
      const colorLabelName = this.gallery().color_labels?.find(l => l.color === color_label)?.name;
      this.analytics.reportKeyboardShortcut('color-label', 'advanced-mode', {
        color: color_label,
        colorLabelName,
        assetsAffected: selectedAssets.length,
      });
    }
  }

  private allowDownload(asset: AssetDto) {
    if (this.galleryPermissions().can_edit) return true;
    const gallery = this.gallery();
    if (!gallery.download_single_asset) return false;
    if (gallery.approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  }

  assetRightClick(asset: AssetDto, event: Event) {
    if (this.allowDownload(asset)) return true;
    event.preventDefault();
    event.stopPropagation();
  }

  private openAsset(asset: AssetDto) {
    const filters = this.filters.length ? JSON.stringify(this.filters) : undefined;
    const search = this.searchTerm;

    const queryParams: Params = { filters, search, autoplay: 'true' };

    this.router.navigate([asset.id], {
      relativeTo: this.route,
      preserveFragment: true,
      queryParams: queryParams,
    });
  }

  openExportDialog(mode?: 'pdf' | 'csv' | 'filenames') {
    this.dialog.open(ExportGalleryDialogComponent, { gallery: this.gallery(), mode });
  }

  private keyDown(event: KeyboardEvent) {
    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.target['tagName'] === 'TEXTAREA') return;

    const metaKey = isMetaKey(event);
    // These events should work even when typing in a field
    if (event.key === '=' || event.key === '+') {
      if (metaKey) {
        event.preventDefault();
        let size = this.assetTargetSize + 50;
        if (size > this.assetMaxSize) size = this.assetMaxSize;
        this.changeAssetSize(size);
      }
    }
    if (event.key === '-' && metaKey) {
      event.preventDefault();
      let size = this.assetTargetSize - 50;
      if (size < this.assetMinSize) size = this.assetMinSize;
      this.changeAssetSize(size);
    }

    if (event.key === '0' && metaKey) {
      event.preventDefault();
      this.changeAssetSize(this.defaultAssetSize);
    }

    if (event.key === 'p' && metaKey) {
      event.preventDefault();
      this.openExportDialog('pdf');
    }

    if (event.key === 'c' && metaKey) {
      event.preventDefault();
      if (this.selectedAssetsCount) this.moveSelectedAssets('copy');
    }
    if (event.key === 'x' && metaKey) {
      event.preventDefault();
      if (this.selectedAssetsCount) this.moveSelectedAssets('move');
    }

    if (event.key === 'e' && metaKey) {
      event.preventDefault();
      this.openExportDialog();
    }

    if (event.key === 'Escape') {
      if (this.header.searchField.nativeElement === document.activeElement) {
        this.header.unfocusSearch();
        this.header.setSearch('');
      } else {
        if (this.selectedAssetsCount) {
          this.unselectAll();
          event.preventDefault();
        } else {
          if (!this.galleryPermissions().can_edit) {
            this.router.navigate(['../'], { relativeTo: this.route, preserveFragment: true });
          }
        }
      }
    }

    if (['Backspace', 'Delete'].includes(event.key)) {
      this.deleteSelectedAssets();
    }

    // These events should NOT work when typing in a field
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target['tagName'])) return;

    if (event.code === 'Space' || event.key === 'Enter') {
      if (!this.firstSelected) return;
      this.openAsset(this.firstSelected);
      event.preventDefault();
    }

    if (event.code === 'KeyA') {
      if (metaKey) {
        this.selectAll();
      } else {
        if (this.selectedCombined) {
          if (this.selectedCombined.status !== 'approved') this.updateSelectedAssetStatus('approved', true);
          else this.updateSelectedAssetStatus('no_status', true);
        }
      }
      event.preventDefault();
    }
    if (event.code === 'KeyX') {
      if (this.selectedCombined) {
        if (this.selectedCombined.status !== 'rejected') this.updateSelectedAssetStatus('rejected', true);
        else this.updateSelectedAssetStatus('no_status', true);
      }
      event.preventDefault();
    }

    if (event.code === 'KeyF') {
      this.toggleLikeSelectedAssets();
      event.preventDefault();
    }

    if (event.code === 'Tab') {
      if (metaKey) {
        event.preventDefault();
        this.header.focusSearch();
      } else this.onSideMenuToggle();
    }

    if (!metaKey) {
      if (event.key === 'r') this.updateSelectedAssetColorLabel('red', true);
      if (event.key === 'o') this.updateSelectedAssetColorLabel('orange', true);
      if (event.key === 'g') this.updateSelectedAssetColorLabel('green', true);
      if (event.key === 'b') this.updateSelectedAssetColorLabel('blue', true);
      if (event.key === 'p') this.updateSelectedAssetColorLabel('purple', true);
      if (event.key === 'n') this.updateSelectedAssetColorLabel(null);
    }

    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      if (!this.lastSelected) return;

      const assets = [].concat(...this.assetGroups.map(g => g.assets));
      const newAsset = getNewAsset(assets, this.assetGroups, this.lastSelected, this.columnCount, event.key);
      if (!newAsset) return;

      if (event.shiftKey) {
        const _range = [
          assets.findIndex(img => img.id === this.firstSelected.id),
          assets.findIndex(img => img.id === newAsset.id),
        ];
        if (_range[0] > _range[1]) _range.reverse();
        const assetsToSelect = assets.filter((img, i) => i >= _range[0] && i <= _range[1]);
        this.unselectAll();
        this.selectAssets(assetsToSelect);
      } else {
        if (!metaKey) this.unselectAll();
        this.selectAsset(newAsset);
      }
    }
  }

  calcInView() {
    if (!this.assets) return;
    if (this.assetPage) return;

    if (this.content) {
      const width = this.content.nativeElement.getBoundingClientRect().width;
      this.cellSize = width / this.columnCount;
    }

    this.inView = {};
    const toPreGenerate: AssetDto[] = [];
    const offset = 100;
    const header = 64;

    const scrollPos = document.scrollingElement.scrollTop;

    this.assetGroups.forEach((group, i) => {
      let groupTop = 0;
      const el = document.getElementById('group-' + i);
      if (el) groupTop = el.offsetTop;

      const groupHeader = this.grouping ? 50 : 0;
      group.assets.forEach((asset, i) => {
        const row = Math.floor(i / this.columnCount);
        const y = groupTop + groupHeader + header + row * this.cellSize;

        const top = y + this.cellSize + offset > scrollPos - this.cellSize;
        const bottom = y - offset < scrollPos + window.innerHeight + this.cellSize;

        const preload =
          top &&
          !bottom &&
          y - offset <
            scrollPos +
              window.innerHeight *
                (assetLoadSettings.galleryScreensToPreloadAfter + assetLoadSettings.galleryScreensToPreGenerate + 1) +
              this.cellSize;

        if (preload) {
          toPreGenerate.push(asset);
        }

        this.inView[asset.id] = top && bottom;
      });
    });

    toPreGenerate.forEach(async asset => {
      if (!canDisplayAsset(asset)) return;
      const { width, height } = getAssetDimentions(asset.version_data);
      const url = this.versionUrlPipe.transform(
        asset.version_data,
        asset.version_data.uuid,
        asset.version_data['status'],
        asset.version_data.watermark_uuid,
        width > height ? this.cellSize * 1.5 : this.cellSize,
        true,
        asset.watermark_enabled
      );
      this.assetPreGenerateService.preloadAsset(url);
    });

    this.check.detectChanges();
  }

  private getSelectedAssets() {
    return this._assets.filter(img => this.selectedAssets.get(img.id));
  }

  async uploadAssets(files: UploadFile[]) {
    if (!this.galleryPermissions().can_edit) return console.warn('Cant upload in client mode');
    let section = this.sections.find(s => s.object === 'asset');
    if (!section) {
      section = await firstValueFrom(
        this.sectionService.createSection(this.gallery().id, 'asset', this.sections.length)
      );
    }

    const assets = await firstValueFrom(this.assetService.getAssetsObservable(section.id, this.gallery().id));
    this.uploadingCount += 1;
    this.uploadService
      .managedUpload(files, this.gallery(), section.id, assets, section.sorting)
      .subscribe(async next => {
        if (next.type === 'created') {
          if (this._assets.find(img => img.id === next.data.asset.id)) return; // avoiding race condition with websocket
          this._assets.push(next.data.asset);
          this.filterChange();
        }

        if (next.type === 'uploaded' && next.data.asset) {
          const gallery = this.gallery();
          if (!gallery.teaser?.uuid) {
            const asset = await this.uploadService.waitForAssetProcessing(next.data.asset.id);
            if (!gallery.teaser?.uuid && canDisplayAsset(asset)) {
              gallery.teaser = { position: '50% 50%' };
              changeCoverAsset(gallery.teaser, asset);

              this.galleryService.updateGallery(gallery.id, {
                teaser: gallery.teaser,
              });
            }
          }
        }
      })
      .add(() => {
        this.uploadingCount -= 1;
        if (!this.uploadingCount) {
          const sectionAssets = this._assets.filter(img => img.section === section.id);
          const sortedAssets = sortAssetSection(sectionAssets, section.sorting);
          if (section.sorting === 'manual') {
            this.assetService.handleOrderChange(sortedAssets, this.gallery().id, section.id);
          }
        }
        this.tenantService.refreshTenant();
        this.sidebar?.createDynamicFilters()?.subscribe();
      });
    this.filterChange();
  }

  uploadAssetEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    this.uploadAssets(Array.from(files));
    if (this.uploadEl) this.uploadEl.nativeElement.value = '';
  }

  private async checkAuth() {
    return this.assetFeedbackService.checkAuth(this.gallery());
  }

  showSelectedActions() {
    if (!this.browserPlatform) return false;
    if (this.allowedAndEnabledFeedbackOptions().includes('favourite')) return true;
    if (this.allowedAndEnabledFeedbackOptions().includes('status')) return true;
    if (this.allowedAndEnabledFeedbackOptions().includes('color_label')) return true;
    return false;
  }

  async moveSelectedAssets(mode?: 'copy' | 'move', gallery = this.gallery()) {
    const assetsToMove = this.getSelectedAssets();

    const resp = await this.dialog.open(MoveAssetsDialogComponent, {
      assets: assetsToMove,
      gallery,
      mode,
    });
    if (resp === 'moved') {
      this._assets = this._assets.filter(img => !assetsToMove.find(_img => img.id === _img.id));
      this.unselectAll();
      this.filterChange();
    }
  }

  async toggleWatermarkSelectedAssets() {
    const assets = this.getSelectedAssets();
    const newValue = !assets[0].watermark_enabled;

    assets
      .filter(img => img.watermark_enabled !== newValue)
      .forEach(asset => {
        asset.watermark_enabled = newValue;
        this.assetService.updateAsset(
          asset,
          { watermark_enabled: newValue },
          this.gallery().id,
          <string>asset.section,
          this.clientMode
        );
      });

    this.filterChange();
    this.calcSelectedCombined();
  }

  assetTouchStart(event: TouchEvent) {
    if (!event.touches[0]) return;
    this.assetTouchStartPos = Rect.fromTouch(event.touches[0]);
  }

  assetTouchMove(event: TouchEvent) {
    const dist = Rect.fromTouch(event.touches[0]).subtract(this.assetTouchStartPos).magnitude();
    if (dist < 10) return;
    // clearTimeout(this.multiTouchTimeout);
  }

  assetTouchEnd() {
    // clearTimeout(this.multiTouchTimeout);
  }

  private getDragging(event: MouseEvent): { index: number; groupIndex: number; pos?: Rect } {
    const pos = Rect.fromMouseEvent(event);

    const groupEls = document.querySelectorAll('.asset-group');
    const groupEl = Array.from(groupEls).find(el => {
      const box = el.getBoundingClientRect();
      if (pos.y < box.y) return false;
      if (pos.y > box.y + box.height) return false;
      if (pos.x < box.x) return false;
      if (pos.x > box.x + box.width) return false;
      return true;
    });

    if (!groupEl) return { index: 0, groupIndex: 0 };

    const groupIndex = Array.from(groupEls).indexOf(groupEl);

    const assetEls = Array.from(groupEl.querySelectorAll('.asset-container-parent'));
    const el = assetEls.find(el => {
      const box = el.getBoundingClientRect();
      if (pos.y < box.y) return false;
      if (pos.y > box.y + box.height) return false;
      if (pos.x < box.x) return false;
      if (pos.x > box.x + box.width) return false;
      return true;
    });

    if (!el) {
      const lastAsset = assetEls[assetEls.length - 1];
      let index = 0;
      let _pos = Rect.fromDOMRect(groupEl.getBoundingClientRect());

      if (lastAsset) {
        const lastBox = lastAsset.getBoundingClientRect();
        _pos = Rect.fromDOMRect(lastBox);
        if (pos.x > lastBox.x) {
          index = assetEls.length;
          _pos.x += _pos.width;
        }
      }

      return { index, groupIndex, pos: _pos };
    }

    let index = assetEls.indexOf(el);

    const box = el.getBoundingClientRect();
    const percent = (1 / box.width) * (pos.x - box.x);
    if (percent > 0.5) {
      index += 1;
      box.x += box.width;
    }
    const _pos = Rect.fromDOMRect(box);

    return { index, groupIndex, pos: _pos };
  }

  startScroll(dir: number) {
    this.scrollingPage = dir;
    this.scroll(dir);
  }

  private async scroll(dir: number) {
    if (this.scrollingPage !== dir) return;
    document.scrollingElement.scrollTop += dir;
    await sleep(20);
    this.scroll(dir);
  }

  stopScroll() {
    this.scrollingPage = 0;
  }

  mouseDown(event: MouseEvent, asset: AssetDto) {
    event.preventDefault();
    if (event.shiftKey) return;
    if (isMetaKey(event)) return;
    if (!this.galleryPermissions().can_edit) return;
    this.potentialDragging = asset;
    this.potentialDraggingStartPos = Rect.fromMouseEvent(event);
  }

  mouseMove(event: MouseEvent) {
    if (event.buttons !== 1) {
      this.dragging = undefined;
      this.potentialDragging = undefined;
      return;
    }
    if (!this.potentialDragging) return;
    event.preventDefault();
    this.mousePos = Rect.fromMouseEvent(event);

    if (this.potentialDraggingStartPos.subtract(this.mousePos).magnitude() < 10) return;

    if ((this.sorting !== 'auto' || (this.grouping && this.grouping !== 'section')) && !this.notifiedAboutSorting) {
      this.notifiedAboutSorting = true;
      this.dragging = undefined;
      this.potentialDragging = undefined;
      if (this.sorting !== 'auto') {
        return this.toasts.add('Dragging only works with "As Layout" sorting', 'info');
      }
      return this.toasts.add('Dragging only works with "Section" grouping', 'info');
    }

    if (!this.dragging) {
      if (!this.selectedAssets.get(this.potentialDragging.id)) {
        this.unselectAll();
        this.selectAsset(this.potentialDragging);
      }
    }
    this.dragging = this.getDragging(event);

    this.stopScroll();
    if (this.mousePos.y - 64 < dragScrollThreshold) {
      this.startScroll(-3);
    } else if (window.innerHeight - this.mousePos.y < dragScrollThreshold) {
      this.startScroll(3);
    }
  }

  async mouseUp(event: MouseEvent) {
    this.notifiedAboutSorting = false;
    if (!this.dragging) return;
    if (!this.dragging.pos) return (this.dragging = undefined);

    event.preventDefault();

    const dragging: {
      index: number;
      groupIndex: number;
      pos?: Rect;
    } = copy(this.dragging);
    this.dragging = undefined;

    const assets = this.getSelectedItems();
    const assetSections = this.sections.filter(section => section.object === 'asset');
    const dropSection = assetSections[dragging.groupIndex];

    const dropGroup = this.assetGroups[dragging.groupIndex];
    const promises: Promise<unknown>[] = [];

    const findTing = (index: number) => {
      if (index === -1) return -1;
      if (this.selectedAssets.get(dropGroup.assets[index].id)) return findTing(index - 1);
      return index;
    };
    const leftAsset = dropGroup.assets[findTing(dragging.index - 1)];

    const oldIndexes = assets.map(asset => {
      const group = this.assetGroups[assetSections.findIndex(s => s.id === asset.section)];
      return group.assets.findIndex(i => i.id === asset.id);
    });

    // Removing assets from old sections
    assets.forEach(asset => {
      const oldGroup = this.assetGroups[assetSections.findIndex(s => s.id === asset.section)];
      const oldIndex = oldGroup.assets.findIndex(i => i.id === asset.id);
      oldGroup.assets.splice(oldIndex, 1);
    });

    const newStartIndex = dropGroup.assets.indexOf(leftAsset) + 1;

    // Adding assets into position of new section
    assets.forEach((asset, i) => {
      const index = newStartIndex + i;
      dropGroup.assets.splice(index, 0, asset);
    });

    const newIndexes = assets.map(asset => {
      const group = this.assetGroups[assetSections.findIndex(s => s.id === asset.section)];
      return group.assets.findIndex(i => i.id === asset.id);
    });

    if (this.sorting !== 'auto') return; // should not happen

    const areChanges =
      !arraysEqual(oldIndexes, newIndexes) || dropGroup.assets.some(asset => asset.section !== dropSection.id);

    if (!areChanges) {
      this.filterChange();
      return;
    }

    if (dropSection.sorting !== 'manual') {
      const assetCount = await firstValueFrom(this.assetService.getAssetCount(this.gallery().id, dropSection.id));
      if (assetCount >= 100) {
        const resp = await this.dialog.open(ManualConfirmDialogComponent);
        if (!resp) {
          this.filterChange();
          return;
        }
      }
      // if the new section is empty, we don't need to set the sorting to manual
      if (assetCount > 0) {
        this.sectionService.updateSection(dropSection, { sorting: 'manual' }).subscribe();
        dropSection.sorting = 'manual';
        this.toasts.add('Asset order changed to Manual', 'info');
      }
    }

    dropGroup.assets.forEach(asset => {
      if (asset.section !== dropSection.id) {
        promises.push(
          this.assetService.updateAsset(
            asset,
            { section: dropSection.id },
            this.gallery().id,
            <string>asset.section,
            this.clientMode
          )
        );
        asset.section = dropSection.id;
      }
    });

    const manualChanges = await this.assetService.handleOrderChange(
      dropGroup.assets,
      this.gallery().id,
      dropSection.id
    );

    manualChanges.forEach(change => {
      const asset = this._assets.find(asset => asset.id === change.asset.id);
      if (asset) asset.position = change.pos;
    });

    this.filterChange();

    // Await all assets to be updated
    await Promise.all(promises);

    // Waiting to avoid deadlock
    // await sleep(50);

    // Await all sections to be fixed
    // await Promise.all(
    //   assetSections.map(section => {
    //     if (section.sorting !== 'manual') return;
    //     return this.assetService.handleSectionAssetsOrderChange(section.id, this.gallery.id);
    //   })
    // );
  }

  getSelectedItems(limit?: number) {
    const assetIds = Array.from(this.selectedAssets.entries())
      .filter(([, v]) => v)
      .map(([k]) => k);
    const assets = this._assets.filter(img => assetIds.includes(img.id));
    if (!limit) return assets;
    return assets.slice(0, limit);
  }

  checkGroups() {
    this.assetGroups.forEach((group, i) => {
      this.selectedGroups['group-' + i] = !group.assets.find(img => !this.selectedAssets.get(img.id));
    });
  }

  handleSelectedGroupChange(group: AssetGroup, selected: boolean) {
    if (selected) this.selectAssets(group.assets);
    else this.unselectAssets(group.assets);
  }

  async setAsCover(asset: AssetDto) {
    const gallery = this.gallery();
    changeCoverAsset(gallery.cover, asset);

    gallery.cover.position = '50% 50%';
    await this.galleryService.updateGallery(gallery.id, {
      cover: gallery.cover,
      theme:
        this.theme().cover.design === null
          ? this.galleryThemeService.getCoverDesignChangeParams(this.theme(), 'simple')
          : undefined,
    });
    this.toasts.add('Asset set as cover', 'success');
  }

  openShareDialog(asset?: AssetDto) {
    if (asset) {
      this.dialog.open(ShareAssetDialogComponent, { gallery: this.gallery(), asset });
    } else {
      this.dialog.open(ShareGalleryDialogComponent, { gallery: this.gallery() });
    }
  }

  private setAdvancedModeAssets() {
    const assets: AssetDto[] = [];
    this.assetGroups.forEach(group => {
      group.assets.forEach(asset => {
        if (this.assets.find(i => i.id === asset.id)) assets.push(asset);
      });
    });
    this.exportGalleryService.currentFilteredAssets = assets;
    this.exportGalleryService.currentSelectedAssets = this.getSelectedItems();
  }

  openWatermarkingSettings() {
    if (this.permissionService.canDisplayElement(billingScopes.asset.watermarking)) {
      return this.dialog.open(GallerySettingsMenuComponent, { gallery: this.gallery() });
    }
    const tenantId = (this.gallery().tenant as TenantDto)?.id || (this.gallery().tenant as string);
    if (this.permissionService.shouldRedirectToPricingPage(tenantId, this.state.activeTenant$?.value)) {
      this.checkoutService.open(billingScopes.asset.watermarking);
    }
  }

  ngOnDestroy() {
    this.exportGalleryService.currentFilteredAssets = undefined;
    this.exportGalleryService.currentSelectedAssets = undefined;
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
